import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    Divider,
    Stack,
    useTheme,
    useMediaQuery
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DeleteIcon from "@mui/icons-material/Delete";
import { formatearDireccion } from "../../helpers/direccion";

/**
 * Modal de confirmación para eliminar recogida
 */
export default function ModalEliminar({ open, recogida, onConfirm, onCancel, loading = false }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : onCancel}
            maxWidth="xs"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 2,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
                    m: isMobile ? 0 : 2
                }
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: { xs: 1, sm: 2 },
                    pb: { xs: 1.5, sm: 2 },
                    pt: { xs: 2, sm: 2.5 },
                    px: { xs: 2, sm: 3 }
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 } }}>
                    <Box
                        sx={{
                            width: { xs: 36, sm: 40 },
                            height: { xs: 36, sm: 40 },
                            borderRadius: "50%",
                            backgroundColor: "#fff5f5",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0
                        }}
                    >
                        <WarningAmberIcon sx={{ color: "#dc3545", fontSize: { xs: 20, sm: 24 } }} />
                    </Box>
                    <Typography
                        variant="h6"
                        sx={{
                            fontWeight: 700,
                            color: "#212529",
                            fontSize: { xs: "1.1rem", sm: "1.25rem" }
                        }}
                    >
                        {isMobile ? "Eliminar" : "Confirmar Eliminación"}
                    </Typography>
                </Box>
                <IconButton
                    onClick={onCancel}
                    disabled={loading}
                    size="small"
                    sx={{
                        color: "#6c757d",
                        "&:hover": {
                            backgroundColor: "rgba(0,0,0,0.04)"
                        }
                    }}
                >
                    <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
                </IconButton>
            </DialogTitle>

            <Divider />

            {/* Content */}
            <DialogContent
                sx={{
                    pt: { xs: 2, sm: 3 },
                    pb: { xs: 2, sm: 2 },
                    px: { xs: 2, sm: 3 },
                    flex: isMobile ? 1 : "initial"
                }}
            >
                <Stack spacing={{ xs: 2, sm: 2.5 }}>
                    {/* Mensaje de confirmación */}
                    <Typography
                        sx={{
                            fontSize: { xs: 14, sm: 15 },
                            color: "#495057",
                            lineHeight: 1.6
                        }}
                    >
                        ¿Estás seguro de que deseas <strong style={{ color: "#dc3545" }}>eliminar</strong> esta recogida?
                    </Typography>

                    {/* Información de la recogida */}
                    {recogida && (
                        <Box
                            sx={{
                                p: { xs: 1.5, sm: 2 },
                                backgroundColor: "#f8f9fa",
                                borderRadius: 2,
                                border: "1px solid #e9ecef"
                            }}
                        >
                            <Stack spacing={1}>
                                <Box>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: 11, sm: 12 },
                                            fontWeight: 600,
                                            color: "#6c757d",
                                            mb: 0.5
                                        }}
                                    >
                                        Ticket:
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: 13, sm: 14 },
                                            fontWeight: 700,
                                            color: "#212529"
                                        }}
                                    >
                                        {recogida.numeroTicket}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: 11, sm: 12 },
                                            fontWeight: 600,
                                            color: "#6c757d",
                                            mb: 0.5
                                        }}
                                    >
                                        Cliente:
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: 13, sm: 14 },
                                            color: "#495057"
                                        }}
                                    >
                                        {recogida.clienteNombre}
                                    </Typography>
                                </Box>

                                <Box>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: 11, sm: 12 },
                                            fontWeight: 600,
                                            color: "#6c757d",
                                            mb: 0.5
                                        }}
                                    >
                                        Dirección:
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: { xs: 13, sm: 14 },
                                            color: "#495057"
                                        }}
                                    >
                                        {formatearDireccion(recogida.direccion)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>
                    )}

                    {/* Advertencia */}
                    <Box
                        sx={{
                            p: { xs: 1.5, sm: 2 },
                            backgroundColor: "#fff5f5",
                            borderRadius: 1,
                            border: "1px solid #f8d7da"
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: { xs: 12, sm: 13 },
                                fontWeight: 600,
                                color: "#842029"
                            }}
                        >
                            ⚠️ Esta acción no se puede deshacer
                        </Typography>
                    </Box>
                </Stack>
            </DialogContent>

            <Divider />

            {/* Actions */}
            <DialogActions
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    gap: { xs: 1, sm: 1.5 },
                    flexDirection: { xs: "column-reverse", sm: "row" }
                }}
            >
                <Button
                    variant="outlined"
                    onClick={onCancel}
                    fullWidth={isMobile}
                    disabled={loading}
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        px: { xs: 2, sm: 2.5 },
                        py: { xs: 1.25, sm: 1 },
                        fontSize: { xs: 14, sm: 15 },
                        color: "#6c757d",
                        borderColor: "#6c757d",
                        "&:hover": {
                            borderColor: "#495057",
                            backgroundColor: "rgba(0,0,0,0.04)"
                        }
                    }}
                >
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    onClick={onConfirm}
                    fullWidth={isMobile}
                    disabled={loading}
                    startIcon={<DeleteIcon sx={{ fontSize: { xs: 18, sm: 20 } }} />}
                    sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        px: { xs: 2, sm: 2.5 },
                        py: { xs: 1.25, sm: 1 },
                        fontSize: { xs: 14, sm: 15 },
                        backgroundColor: "#dc3545",
                        "&:hover": {
                            backgroundColor: "#bb2d3b"
                        },
                        "&:disabled": {
                            backgroundColor: "#6c757d",
                            color: "#fff"
                        }
                    }}
                >
                    {loading ? "Eliminando..." : "Eliminar"}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
